import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getProspections,
  getProspectionsKpis,
  getDistinctDisciplines,
  getDistinctDepartements,
  createProspection,
  updateProspection,
  deleteProspection,
  updateProspectionStatus,
  exportProspectionsCsv,
  bulkImportProspections,
} from "../../../../services/prospectionServices";

import { getAllDesinscriptions } from "../../../../services/desinscriptionServices";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ProspectionHero from "./components/ProspectionHero";
import ComplianceNotice from "./components/ComplianceNotice";
import ImportProspectsModal from "./components/ImportProspectsModal";

import ProspectionModal from "./components/ProspectionModal";
import QuickViewModal from "./components/QuickViewModal";
import CreateCampaignModal from "./components/CreateCampaignModal";
import CreateTemplateModal from "./components/CreateTemplateModal";
import CreateSenderModal from "./components/CreateSenderModal";

import ProspectsTab from "./tabs/ProspectsTab";
import CampaignsTab from "./tabs/CampaignsTab";
import TemplatesTab from "./tabs/TemplatesTab";
import SendersTab from "./tabs/SendersTab";

import { DEFAULT_LIMIT, STATUTS_CANON } from "./constants";
import {
  fmtDateFR,
  nowLocalDateTimeSql,
  parseCsv,
  parseEmails,
  todayLocalISO,
} from "./utils";

const KPI_ALL_FALLBACK = {
  total: 0,
  clients: 0,
  prospects: 0,
  interest: 0,
  desinscritsStatut: 0,
};

const makeActivityKey = (date) => `prospection_activity_${date}`;

const initTodayActivity = (date = todayLocalISO()) => ({
  date,
  updatedIds: [],
  contactedIds: [],
  contactsByDiscipline: {},
});

const normDiscipline = (d) => {
  const s = String(d || "").trim();
  return s || "Sans discipline";
};

const buildStatutParam = (statuts = []) => {
  const clean = Array.isArray(statuts)
    ? statuts.map((s) => String(s || "").trim()).filter(Boolean)
    : [];

  return clean.length ? clean.join(",") : undefined;
};

async function copyToClipboard(text) {
  if (!text) return false;

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

export default function ProspectionPage() {
  const [activeTab, setActiveTab] = useState("prospects");

  // DATA PROSPECTS
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_LIMIT,
    total: 0,
    pages: 1,
  });

  const [kpisAll, setKpisAll] = useState(KPI_ALL_FALLBACK);

  // FILTERS
  const [search, setSearch] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [departement, setDepartement] = useState("");
  const [statuts, setStatuts] = useState([]);
  const [siteStatus, setSiteStatus] = useState("");
  const [emailMode, setEmailMode] = useState("all");
  const [licMin, setLicMin] = useState("");
  const [licMax, setLicMax] = useState("");
  const [sort, setSort] = useState("-updated_at");
  const [hideUnsubStatus, setHideUnsubStatus] = useState(true);

  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [campaignRefreshKey, setCampaignRefreshKey] = useState(0);

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [templateRefreshKey, setTemplateRefreshKey] = useState(0);

  const [showSenderModal, setShowSenderModal] = useState(false);
  const [editSender, setEditSender] = useState(null);
  const [senderRefreshKey, setSenderRefreshKey] = useState(0);

  const [disciplines, setDisciplines] = useState([]);
  const [departements, setDepartements] = useState([]);

  // MODALS
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [quickItem, setQuickItem] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importRows, setImportRows] = useState([]);

  const [campaignSeed, setCampaignSeed] = useState(null);

  // SELECTION
  const [selectedIds, setSelectedIds] = useState(new Set());

  // DESINSCRIPTIONS
  const [unsubSet, setUnsubSet] = useState(new Set());
  const [unsubMap, setUnsubMap] = useState(new Map());

  // ACTIVITÉ DU JOUR
  const [todayActivity, setTodayActivity] = useState(() => {
    const date = todayLocalISO();

    try {
      const raw = localStorage.getItem(makeActivityKey(date));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.date === date) {
          return { ...initTodayActivity(date), ...parsed };
        }
      }
    } catch {}

    return initTodayActivity(date);
  });

  const saveTodayActivity = useCallback((next) => {
    try {
      localStorage.setItem(makeActivityKey(next.date), JSON.stringify(next));
    } catch {}
  }, []);

  const recordTodayActivity = useCallback(
    ({ updateIds = [], contactRows = [] }) => {
      setTodayActivity((prev) => {
        const date = todayLocalISO();
        const base = prev?.date === date ? prev : initTodayActivity(date);

        const updated = new Set(base.updatedIds || []);
        const contacted = new Set(base.contactedIds || []);
        const byDisc = { ...(base.contactsByDiscipline || {}) };

        let changed = false;

        for (const id of updateIds) {
          if (id != null && !updated.has(id)) {
            updated.add(id);
            changed = true;
          }
        }

        for (const r of contactRows) {
          const id = r?.id;
          if (id == null) continue;

          if (!contacted.has(id)) {
            contacted.add(id);
            const disc = normDiscipline(r.discipline);
            byDisc[disc] = (byDisc[disc] || 0) + 1;
            changed = true;
          }
        }

        if (!changed) return base;

        const next = {
          ...base,
          date,
          updatedIds: Array.from(updated),
          contactedIds: Array.from(contacted),
          contactsByDiscipline: byDisc,
        };

        saveTodayActivity(next);
        return next;
      });
    },
    [saveTodayActivity],
  );

  useEffect(() => {
    const id = setInterval(() => {
      const date = todayLocalISO();

      setTodayActivity((prev) => {
        if (prev?.date === date) return prev;
        return initTodayActivity(date);
      });
    }, 60_000);

    return () => clearInterval(id);
  }, []);

  const searchRef = useRef();

  useEffect(() => {
    if (searchRef.current) clearTimeout(searchRef.current);

    searchRef.current = setTimeout(() => {
      fetchData(1, pagination.limit);
    }, 350);

    return () => searchRef.current && clearTimeout(searchRef.current);
    // eslint-disable-next-line
  }, [search]);

  const fetchFilters = useCallback(async () => {
    try {
      const [d1, d2] = await Promise.all([
        getDistinctDisciplines(),
        getDistinctDepartements(),
      ]);

      setDisciplines(d1 || []);
      setDepartements(d2 || []);
    } catch {}
  }, []);

  const fetchUnsubs = useCallback(async () => {
    try {
      const list = await getAllDesinscriptions();

      const set = new Set();
      const map = new Map();

      (list || []).forEach(({ email, date_desinscription }) => {
        const e = String(email || "").trim().toLowerCase();

        if (e) {
          set.add(e);
          map.set(e, date_desinscription || null);
        }
      });

      setUnsubSet(set);
      setUnsubMap(map);
    } catch {}
  }, []);

  const baseParams = useMemo(
    () => ({
      search,
      discipline: discipline || undefined,
      departement: departement || undefined,

      // Multi-statuts envoyés en CSV :
      // "Prospect,Prospect +,Intérêt"
      statut: buildStatutParam(statuts),

      site_status: siteStatus || undefined,
      has_email:
        emailMode === "with" ? "1" : emailMode === "without" ? "0" : undefined,
      lic_min: licMin !== "" ? licMin : undefined,
      lic_max: licMax !== "" ? licMax : undefined,
      sort,
      hide_unsub_status: hideUnsubStatus ? "1" : undefined,
    }),
    [
      search,
      discipline,
      departement,
      statuts,
      siteStatus,
      emailMode,
      licMin,
      licMax,
      sort,
      hideUnsubStatus,
    ],
  );

  const fetchData = useCallback(
    async (page = 1, limit = DEFAULT_LIMIT, overrideBaseParams) => {
      try {
        setLoading(true);

        const params = overrideBaseParams || baseParams;

        const [res, totals] = await Promise.all([
          getProspections({ page, limit, ...params }),
          getProspectionsKpis(params),
        ]);

        setKpisAll(totals || KPI_ALL_FALLBACK);

        if (res?.data) {
          setRows(res.data);
          setPagination(res.pagination);
          setSelectedIds(new Set());
        } else {
          setRows([]);
          setPagination({
            page: 1,
            limit,
            total: 0,
            pages: 1,
          });
          setSelectedIds(new Set());
        }
      } catch {
        toast.error("Erreur lors du chargement des prospections.");
      } finally {
        setLoading(false);
      }
    },
    [baseParams],
  );

  useEffect(() => {
    fetchFilters();
    fetchUnsubs();
    fetchData(1, DEFAULT_LIMIT);
    // eslint-disable-next-line
  }, []);

  const annotatedRows = useMemo(() => {
    if (!rows?.length) return [];

    return rows.map((r) => {
      const mails = parseEmails(r.email).map((e) => e.toLowerCase());
      const hit = mails.find((e) => unsubSet.has(e));

      if (hit) {
        return {
          ...r,
          _isUnsub: true,
          _unsubEmail: hit,
          _unsubDate: unsubMap.get(hit) || null,
        };
      }

      return {
        ...r,
        _isUnsub: false,
      };
    });
  }, [rows, unsubSet, unsubMap]);

  const visibleRows = useMemo(() => {
    const list = annotatedRows || [];

    if (!hideUnsubStatus) return list;

    return list.filter((r) => (r.statut || "") !== "Désinscrit");
  }, [annotatedRows, hideUnsubStatus]);

  useEffect(() => {
    if (!hideUnsubStatus) return;

    setSelectedIds((prev) => {
      if (!prev?.size) return prev;

      const visibleIdSet = new Set(visibleRows.map((r) => r.id));
      const next = new Set([...prev].filter((id) => visibleIdSet.has(id)));

      return next;
    });
  }, [hideUnsubStatus, visibleRows]);

  const pageKpis = useMemo(() => {
    const pageCount = annotatedRows.length;
    const counts = Object.fromEntries(STATUTS_CANON.map((s) => [s, 0]));

    for (const r of annotatedRows) {
      const s = r.statut || "";
      if (s in counts) counts[s]++;
    }

    const unsubCount = annotatedRows.filter((r) => r._isUnsub).length;

    return {
      pageCount,
      unsubDetected: unsubCount,
      clients: counts["Client"] || 0,
      prospects: (counts["Prospect"] || 0) + (counts["Prospect +"] || 0),
      interest: counts["Intérêt"] || 0,
      desinscritsStatut: counts["Désinscrit"] || 0,
    };
  }, [annotatedRows]);

  const resetFilters = () => {
    setSearch("");
    setDiscipline("");
    setDepartement("");
    setStatuts([]);
    setSiteStatus("");
    setEmailMode("all");
    setLicMin("");
    setLicMax("");
    setSort("-updated_at");
    setHideUnsubStatus(true);

    fetchData(1, DEFAULT_LIMIT, {
      search: "",
      discipline: undefined,
      departement: undefined,
      statut: undefined,
      site_status: undefined,
      has_email: undefined,
      lic_min: undefined,
      lic_max: undefined,
      sort: "-updated_at",
      hide_unsub_status: "1",
    });
  };

  const changePage = (newPage) => {
    if (newPage < 1 || newPage > (pagination?.pages || 1)) return;
    fetchData(newPage, pagination.limit);
  };

  const changeLimit = (newLimit) => {
    fetchData(1, newLimit);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette fiche ?")) return;

    try {
      await deleteProspection(id);

      setRows((state) => state.filter((r) => r.id !== id));

      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      fetchData(pagination.page, pagination.limit);
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const onStatusChange = async (row, newStatut) => {
    const old = row.statut;

    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, statut: newStatut } : r)),
    );

    try {
      await updateProspectionStatus(row.id, { statut: newStatut });
      recordTodayActivity({ updateIds: [row.id] });
      fetchData(pagination.page, pagination.limit);
    } catch {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, statut: old } : r)),
      );
      toast.error("Échec de la mise à jour du statut.");
    }
  };

  const onQuickCopyEmailAndDate = async (row) => {
    try {
      const mails = parseEmails(row.email || "");

      if (!mails.length) {
        toast.warning("Aucun email pour ce prospect.");
        return;
      }

      if (row._isUnsub || row.statut === "Désinscrit") {
        toast.warning("Ce contact est désinscrit : email non copié.");
        return;
      }

      const primary = mails[0];
      const copied = await copyToClipboard(primary);

      if (!copied) {
        toast.error("Impossible de copier l’email.");
        return;
      }

      const now = nowLocalDateTimeSql();
      const prevDate = row.date_contact || null;

      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, date_contact: now } : r)),
      );

      try {
        await updateProspection(row.id, {
          date_contact: now,
          contacte_par: "Copie email",
        });

        recordTodayActivity({
          updateIds: [row.id],
          contactRows: [{ ...row, date_contact: now }],
        });

        fetchData(pagination.page, pagination.limit);

        toast.success(
          `Email copié et contact daté au ${fmtDateFR(now)}.`,
        );
      } catch {
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id ? { ...r, date_contact: prevDate } : r,
          ),
        );

        toast.error("La date de contact n’a pas pu être mise à jour.");
      }
    } catch {
      toast.error("Impossible d’effectuer l’action.");
    }
  };

  const onCopySelectedEmails = async () => {
    const selectedRows = visibleRows.filter((r) => selectedIds.has(r.id));

    if (!selectedRows.length) {
      toast.warning("Sélectionnez au moins un prospect.");
      return;
    }

    const seen = new Set();
    const emails = [];
    let skippedUnsub = 0;
    let skippedEmpty = 0;

    for (const row of selectedRows) {
      const rowEmails = parseEmails(row.email || "");

      if (!rowEmails.length) {
        skippedEmpty++;
        continue;
      }

      for (const email of rowEmails) {
        const key = email.trim().toLowerCase();

        if (!key) continue;

        if (row.statut === "Désinscrit" || unsubSet.has(key)) {
          skippedUnsub++;
          continue;
        }

        if (!seen.has(key)) {
          seen.add(key);
          emails.push(email.trim());
        }
      }
    }

    if (!emails.length) {
      toast.warning("Aucun email valide à copier dans la sélection.");
      return;
    }

    const copied = await copyToClipboard(emails.join("; "));

    if (!copied) {
      toast.error("Impossible de copier les emails.");
      return;
    }

    const details = [
      `${emails.length} email(s) copié(s)`,
      skippedUnsub ? `${skippedUnsub} désinscrit(s) ignoré(s)` : null,
      skippedEmpty ? `${skippedEmpty} fiche(s) sans email` : null,
    ]
      .filter(Boolean)
      .join(" • ");

    toast.success(details);
  };

  const onExport = async () => {
    try {
      const blob = await exportProspectionsCsv(baseParams);

      if (!blob) throw new Error();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `prospections_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}.csv`;

      a.click();

      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors de l’export CSV.");
    }
  };

  const onFile = (file) => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      setImportRows(parseCsv(e.target.result));
    };

    reader.readAsText(file, "utf-8");
  };

  const doImport = async () => {
    try {
      if (!importRows.length) return;

      const res = await bulkImportProspections(importRows, "upsert");

      if (!res) throw new Error();

      setShowImport(false);
      setImportRows([]);

      fetchData(1, pagination.limit);
      toast.success("Import terminé.");
    } catch {
      toast.error("Import échoué.");
    }
  };

  const createCampaignFromFilters = () => {
    const seed = {
      mode: "filters",
      filters: {
        ...baseParams,
        has_email: "1",
      },
      selectedIds: [],
    };

    setCampaignSeed(seed);
    setActiveTab("campaigns");
    setShowCreateCampaignModal(true);
  };

  const createCampaignFromSelection = () => {
    const ids = Array.from(selectedIds);

    if (!ids.length) {
      toast.warning("Sélectionnez au moins un prospect.");
      return;
    }

    const seed = {
      mode: "selection",
      filters: null,
      selectedIds: ids,
    };

    setCampaignSeed(seed);
    setActiveTab("campaigns");
    setShowCreateCampaignModal(true);
  };

  const openCreateCampaign = () => {
    const seed = {
      mode: "manual",
      filters: {
        ...baseParams,
        has_email: "1",
      },
      selectedIds: [],
    };

    setCampaignSeed(seed);
    setActiveTab("campaigns");
    setShowCreateCampaignModal(true);
  };

  const openCreateTemplate = () => {
    setEditTemplate(null);
    setShowTemplateModal(true);
  };

  const openEditTemplate = (template) => {
    setEditTemplate(template);
    setShowTemplateModal(true);
  };

  const openCreateSender = () => {
    setEditSender(null);
    setShowSenderModal(true);
  };

  const openEditSender = (sender) => {
    setEditSender(sender);
    setShowSenderModal(true);
  };

  return (
    <div className="space-y-6">
      <ProspectionHero
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        kpisAll={kpisAll}
        pagination={pagination}
        loading={loading}
        pageKpis={pageKpis}
      />

      <ComplianceNotice />

      {activeTab === "prospects" && (
        <ProspectsTab
          loading={loading}
          visibleRows={visibleRows}
          pagination={pagination}
          baseParams={baseParams}
          search={search}
          setSearch={setSearch}
          discipline={discipline}
          setDiscipline={setDiscipline}
          departement={departement}
          setDepartement={setDepartement}
          statuts={statuts}
          setStatuts={setStatuts}
          siteStatus={siteStatus}
          setSiteStatus={setSiteStatus}
          emailMode={emailMode}
          setEmailMode={setEmailMode}
          licMin={licMin}
          setLicMin={setLicMin}
          licMax={licMax}
          setLicMax={setLicMax}
          sort={sort}
          setSort={setSort}
          disciplines={disciplines}
          departements={departements}
          hideUnsubStatus={hideUnsubStatus}
          setHideUnsubStatus={setHideUnsubStatus}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onApplyFilters={() => fetchData(1, pagination.limit)}
          onResetFilters={resetFilters}
          onRefresh={() => fetchData(pagination.page, pagination.limit)}
          onChangeLimit={changeLimit}
          onChangePage={changePage}
          onAdd={() => setShowAdd(true)}
          onImport={() => setShowImport(true)}
          onExport={onExport}
          onCopySelectedEmails={onCopySelectedEmails}
          onEdit={setEditItem}
          onDelete={onDelete}
          onQuickView={setQuickItem}
          onStatusChange={onStatusChange}
          onQuickCopy={onQuickCopyEmailAndDate}
          onCreateCampaignFromFilters={createCampaignFromFilters}
          onCreateCampaignFromSelection={createCampaignFromSelection}
        />
      )}

      {activeTab === "campaigns" && (
        <CampaignsTab
          campaignSeed={campaignSeed}
          refreshKey={campaignRefreshKey}
          onOpenCreateCampaign={openCreateCampaign}
        />
      )}

      {activeTab === "templates" && (
        <TemplatesTab
          refreshKey={templateRefreshKey}
          onCreateTemplate={openCreateTemplate}
          onEditTemplate={openEditTemplate}
        />
      )}

      {activeTab === "senders" && (
        <SendersTab
          refreshKey={senderRefreshKey}
          onCreateSender={openCreateSender}
          onEditSender={openEditSender}
        />
      )}

      {showAdd && (
        <ProspectionModal
          title="Ajouter une prospection"
          onClose={() => setShowAdd(false)}
          prefill={{
            discipline,
            departement_code: departement,
          }}
          onSubmit={async (payload) => {
            const ok = await createProspection(payload);

            if (ok) {
              setShowAdd(false);
              fetchData(1, pagination.limit);
              toast.success("Prospect ajouté.");
            } else {
              toast.error("Création impossible.");
            }
          }}
        />
      )}

      {editItem && (
        <ProspectionModal
          title={`Éditer : ${editItem.nom_club || "—"}`}
          initial={editItem}
          onClose={() => setEditItem(null)}
          onSubmit={async (payload) => {
            const ok = await updateProspection(editItem.id, payload);

            if (ok) {
              recordTodayActivity({ updateIds: [editItem.id] });
              setEditItem(null);
              fetchData(pagination.page, pagination.limit);
              toast.success("Prospect mis à jour.");
            } else {
              toast.error("Mise à jour impossible.");
            }
          }}
        />
      )}

      {quickItem && (
        <QuickViewModal data={quickItem} onClose={() => setQuickItem(null)} />
      )}

      {showImport && (
        <ImportProspectsModal
          importRows={importRows}
          onFile={onFile}
          onImport={doImport}
          onClose={() => {
            setShowImport(false);
            setImportRows([]);
          }}
        />
      )}

      {showCreateCampaignModal && (
        <CreateCampaignModal
          campaignSeed={campaignSeed}
          onClose={() => setShowCreateCampaignModal(false)}
          onCreated={({ close }) => {
            setCampaignRefreshKey((key) => key + 1);

            if (close) {
              setShowCreateCampaignModal(false);
            }

            toast.success("Campagne créée et préparée.");
          }}
        />
      )}

      {showTemplateModal && (
        <CreateTemplateModal
          initial={editTemplate}
          onClose={() => {
            setShowTemplateModal(false);
            setEditTemplate(null);
          }}
          onSaved={() => {
            setTemplateRefreshKey((key) => key + 1);
            toast.success("Modèle d’email sauvegardé.");
          }}
        />
      )}

      {showSenderModal && (
        <CreateSenderModal
          initial={editSender}
          onClose={() => {
            setShowSenderModal(false);
            setEditSender(null);
          }}
          onSaved={() => {
            setSenderRefreshKey((key) => key + 1);
            toast.success("Compte expéditeur sauvegardé.");
          }}
        />
      )}

      <ToastContainer />
    </div>
  );
}